import React, { useMemo } from 'react';
import { FormGrid } from '@packages/ui/patterns';
import { Typography } from '@packages/ui/primitives';
import { Stack } from '@packages/ui/layout';
import type { FormEachNode } from '../../types/layout';
import { FormRowExcludedValuesProvider } from '../../context/FormRowContext';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { FormEachRowActionsButton } from './each/FormEachRowActionsButton';
import { FormEachRowHeader } from './each/FormEachRowHeader';
import { FormFieldAdapter } from './FormFieldAdapter';
import { FormRowProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useEffectProcessor } from '../../processors/effectProcessor';
import { useObserverProcessor } from '../../processors/observerProcessor';
import { useRowObserverProcessor } from '../../processors/rowObserverProcessor';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { prefixEffects, prefixObservers } from '../../processors/pathRewriter';
import { resolveItemTokens } from '../../resolver/tokenResolver';
import type { FormFieldMapping } from '../../types/fieldMapping';
import { rem } from '@packages/ui/tokens';
import { evaluateCondition } from '../../evaluators/conditionEvaluator';
import { FormEachBandedTableRenderer } from './FormEachBandedTableRenderer';
import { usesBandedTablePresentation } from './each/resolveTablePresentation';

interface FormEachTableRendererProps {
  node: FormEachNode;
}

export const FormEachTableRenderer = React.memo(function FormEachTableRenderer({
  node,
}: FormEachTableRendererProps): React.ReactElement {
  if (usesBandedTablePresentation(node)) {
    return <FormEachBandedTableRenderer node={node} />;
  }

  return <FormEachPlainTableRenderer node={node} />;
});

/** Default `presentation: "table"` — FormGrid layout (unchanged unless `tableHeaderStyle: "banded"`). */
const FormEachPlainTableRenderer = React.memo(function FormEachPlainTableRenderer({
  node,
}: FormEachTableRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly } = useFormEngineState();
  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
    itemRules: node.itemRules,
    revalidateOnPerRowSync: node.revalidateOnPerRowSync,
    revalidateTargetsOnPerRowSync: node.revalidateTargetsOnPerRowSync,
  });

  const hasRowActions = (node.rowActions ?? []).length > 0;

  const addPosition = resolveAddButtonPosition(node);
  const addControl = showAdd ? <FormEachAddButton node={node} onClick={handleAdd} /> : null;
  const showHeaders = node.showTableHeaders !== false;
  const showRemove = canRemoveRow();
  const showReset = useMemo(() => {
    if (node.resetButton == null || isReadOnly) return false;
    const vw = node.resetButton.visibleWhen;
    if (vw == null) return true;
    const currentValues = form.getValues() as Record<string, unknown>;
    return evaluateCondition(vw, { values: currentValues, meta, getOptionProp });
  }, [node.resetButton, isReadOnly, fields.length, form, meta, getOptionProp]);
  const showActionsCell = showRemove || showReset || hasRowActions;

  const hasInlineHeader = addPosition === 'top-right' && (node.title != null || addControl != null);

  const tableGridTemplate = useMemo(() => {
    const colDefs = node.itemFields.map((f) => {
      const min = f.matrix?.minWidthPx ?? 120;
      const max = f.matrix?.maxWidthPx;
      const maxTrack = max != null ? `${max}px` : '1fr';
      return `minmax(${min}px, ${maxTrack})`;
    });
    if (showActionsCell) colDefs.push('40px');
    return colDefs.join(' ');
  }, [node.itemFields, showActionsCell]);

  const totalMinWidth = useMemo(() => {
    const fieldWidths = node.itemFields.reduce((sum, f) => sum + (f.matrix?.minWidthPx ?? 120), 0);
    const actionWidth = showActionsCell ? 40 : 0;
    const gapWidth = (node.itemFields.length + (showActionsCell ? 1 : 0) - 1) * 16;
    return fieldWidths + actionWidth + gapWidth;
  }, [node.itemFields, showActionsCell]);

  return (
    <Stack gap='var(--ui-space-md, 16px)' style={{ marginTop: '8px' }}>
      {hasInlineHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}>
          {node.title != null && <Typography variant='h5'>{node.title}</Typography>}
          {addControl}
        </div>
      )}

      <div style={{ overflowX: 'auto', width: '100%', paddingBottom: '8px' }}>
        <FormGrid
          columns={{ base: tableGridTemplate }}
          columnGap={rem(16)}
          rowGap='4px'
          style={{ minWidth: `${totalMinWidth}px` }}
          data-testid='each-table-plain-grid'>
          {showHeaders && (
            <>
              {node.rowHeader?.style != null && node.rowHeader.style !== 'none' && (
                <FormGrid.Item colSpan={1}>
                  <Typography variant='body-s'>#</Typography>
                </FormGrid.Item>
              )}
              {node.itemFields.map((field) => (
                <FormGrid.Item key={`header_${field.name}`} colSpan={field.colSpan ?? 1}>
                  <Typography variant='body-s'>{field.def.label}</Typography>
                </FormGrid.Item>
              ))}
              {showActionsCell && (
                <FormGrid.Item colSpan={1}>
                  <Typography variant='body-s' />
                </FormGrid.Item>
              )}
            </>
          )}

          {fields.length === 0 && node.emptyState != null && (
            <FormGrid.Item colSpan={node.itemFields.length + (showActionsCell ? 1 : 0)}>
              <Typography variant='body-s'>{node.emptyState.title}</Typography>
              {node.emptyState.description != null && (
                <Typography variant='body-xs' color='var(--ui-text-muted)'>
                  {node.emptyState.description}
                </Typography>
              )}
            </FormGrid.Item>
          )}

          {fields.map((item, index) => (
            <PlainTableEachRow
              key={item.id}
              node={node}
              index={index}
              total={fields.length}
              showRemove={showRemove}
              showActionsCell={showActionsCell}
              hasRowActions={hasRowActions}
              excludedValues={buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields)}
              rowValues={allRowValues?.[index] as Record<string, unknown> | undefined}
              onRemove={handleRemove}
            />
          ))}
        </FormGrid>
      </div>

      {!hasInlineHeader && addControl}
    </Stack>
  );
});

interface PlainTableEachRowProps {
  node: FormEachNode;
  index: number;
  total: number;
  showRemove: boolean;
  showActionsCell: boolean;
  hasRowActions: boolean;
  excludedValues: Record<string, unknown[]>;
  rowValues?: Record<string, unknown>;
  onRemove: (index: number) => void;
}

const PlainTableEachRow = React.memo(function PlainTableEachRow({
  node,
  index,
  total,
  showRemove,
  showActionsCell,
  hasRowActions,
  excludedValues,
  rowValues,
  onRemove,
}: PlainTableEachRowProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const namePrefix = `${node.name}.${index}`;

  const rowMeta = useMemo(
    () => ({
      index,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      documentCategory: node.documentCategory,
      tablePresentation: true,
    }),
    [index, total, node.documentCategory],
  );

  const resolvedItemFields = useMemo(() => {
    return node.itemFields.map((field) =>
      resolveItemTokens(field, namePrefix) as FormFieldMapping,
    );
  }, [node.itemFields, namePrefix]);

  const scopedEffects = useMemo(
    () => (node.itemEffects?.length ? prefixEffects(node.itemEffects, namePrefix) : []),
    [node.itemEffects, namePrefix],
  );

  const scopedObservers = useMemo(
    () => (node.itemObservers?.length ? prefixObservers(node.itemObservers, namePrefix) : []),
    [node.itemObservers, namePrefix],
  );

  useEffectProcessor({
    form: form as never,
    effects: scopedEffects,
    meta,
    getOptionProp,
    programmaticWriteGuard,
  });
  useObserverProcessor({ form: form as never, observers: scopedObservers, meta, getOptionProp });
  useRowObserverProcessor({
    form: form as never,
    observers: node.itemRowObservers ?? [],
    rowPrefix: namePrefix,
    meta,
    getOptionProp,
  });

  return (
    <FormRowExcludedValuesProvider value={excludedValues}>
      <FormRowProvider value={rowMeta}>
        {node.rowHeader?.style != null && node.rowHeader.style !== 'none' && (
          <FormGrid.Item colSpan={1}>
            <FormEachRowHeader config={node.rowHeader} index={index} rowValues={rowValues} />
          </FormGrid.Item>
        )}
        {resolvedItemFields.map((field, fieldIndex) => (
          <FormGrid.Item key={field.name || `__table_row_${fieldIndex}`} colSpan={field.colSpan}>
            <FormFieldAdapter field={field} namePrefix={namePrefix} />
          </FormGrid.Item>
        ))}
        {showActionsCell && (
          <FormGrid.Item colSpan={1}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {node.resetButton != null && (
                <FormEachResetButton node={node} index={index} arrayLength={total} />
              )}
              {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={onRemove} />}
              {hasRowActions && (
                <FormEachRowActionsButton node={node} index={index} rowValues={rowValues} />
              )}
            </div>
          </FormGrid.Item>
        )}
      </FormRowProvider>
    </FormRowExcludedValuesProvider>
  );
});
